import React, { useState, useEffect } from 'react';
import Modal from '../../elements/Modal';
import { fetchUsers, getProjects, updateProperty } from '../../utils/api/api';
import toast from 'react-hot-toast';

const PropertyModal = ({ property, onClose }) => {
    const [editedProperty, setEditedProperty] = useState({ ...property });
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await getProjects();
                setProjects(response.projects);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };

        const getUsers = async () => {
            try {
                const response = await fetchUsers();
                setUsers(response.users);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchProjects();
        getUsers();
    }, []);

    const handleChange = (e) => {
        setEditedProperty({
            ...editedProperty,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const updatedPropertyData = {
            id: editedProperty.id,
            project_id: parseInt(editedProperty.project_id, 10),
            property_number: editedProperty.property_number,
            title_number: editedProperty.title_number,
            assigned_to: editedProperty.assigned_to ? parseInt(editedProperty.assigned_to, 10) : null,
            size: editedProperty.size,
            status: editedProperty.status,
        };

        try {
            await updateProperty(updatedPropertyData);
            toast.success("Property updated");
            onClose();
        } catch (error) {
            toast.error('Failed to update property');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const inputStyles = `border bg-transparent w-full border-gray-300 rounded-md p-2 outline-none 
        focus:border-secondary transition-all duration-200 focus:ring-1 focus:ring-secondary text-sm`;

    const labelStyles = (value) => `absolute left-2 text-xs text-gray-600 transition-all duration-200 
        transform -translate-y-4 ${value ? 'text-xs text-secondary' : 'text-sm text-gray-500'}`;

    return (
        <Modal onClose={onClose} title="Edit Property">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative">
                    <label className={labelStyles(editedProperty.title_number)}>Title Number</label>
                    <input
                        type="text"
                        name="title_number"
                        value={editedProperty.title_number || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    />
                </div>
                <div className="relative">
                    <label className={labelStyles(editedProperty.property_number)}>Property Number</label>
                    <input
                        type="text"
                        name="property_number"
                        value={editedProperty.property_number || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    />
                </div>
                <div className="relative">
                    <label className={labelStyles(editedProperty.project_id)}>Project</label>
                    <select
                        name="project_id"
                        value={editedProperty.project_id || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    >
                        <option value="">Select Project</option>
                        {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                                {project.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="relative">
                    <label className={labelStyles(editedProperty.assigned_to)}>Assigned To</label>
                    <select
                        name="assigned_to"
                        value={editedProperty.assigned_to || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    >
                        <option value="">Select User</option>
                        {users.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user?.first_name} {user?.last_name} - {user?.role?.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="relative">
                    <label className={labelStyles(editedProperty.size)}>Size</label>
                    <input
                        type="text"
                        name="size"
                        value={editedProperty.size || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    />
                </div>
                <div className="relative">
                    <label className={labelStyles(editedProperty.status)}>Status</label>
                    <select
                        name="status"
                        value={editedProperty.status || ''}
                        onChange={handleChange}
                        required
                        className={inputStyles}
                    >
                        <option value="available">Available</option>
                        <option value="sold">Sold</option>
                        <option value="under_contract">Under Contract</option>
                        <option value="booked">Booked</option>
                        <option value="reserved">Reserved</option>
                    </select>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-300 px-6 text-[13px] py-1.5 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`bg-secondary text-white px-6 text-[13px] py-1.5 rounded-lg ${loading ? 'opacity-50' : 'hover:bg-blue-600'}`}
                    >
                        {loading ? 'Saving' : 'Save'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default PropertyModal;
